import { useContext, useEffect } from 'react'

import { DateTime } from 'luxon'

import {
  digObject, formatDate, launchModal, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useThunkDispatch } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useCampaignContributions from '@hooks/useCampaignContributions'
import useCampaignItems from '@hooks/useCampaignItems'
import useCreditNotes from '@hooks/useCreditNotes'
import useOrderItems from '@hooks/useOrderItems'
import useOrders from '@hooks/useOrders'
import useSelector from '@hooks/useSelector'

import type {
  CampaignItemModel, OrderItemModel, OrderModel, ReconciliationItemModel,
} from '@models/types'

import type { EntitiesState } from '@redux/entities'
import { updateCampaignItem } from '@redux/modules/campaignItem'
import type { EntityOptions } from '@redux/modules/module'
import { updateOrder } from '@redux/modules/order'
import type { AppDispatch } from '@redux/store'

import useSelectEntities, { UseSelectEntitiesPayload } from './useSelectEntities'

type SelectAllItemsParams = {
  items: ReconciliationItemModel[],
  selectAll: UseSelectEntitiesPayload['callbacks']['selectAll'],
  selectedEntityType: string,
}

const selectAllItems = (params: SelectAllItemsParams) => {
  const { items, selectAll, selectedEntityType } = params

  const validItems = items.filter(
    (item) => {
      if (item.itemType !== 'processing-fees' && selectedEntityType === item.itemType
      && !item.invoice_reference) return item
    },
  )

  selectAll(validItems)
}

const mapCampaignItems = (campaignItems: CampaignItemModel[]) => campaignItems.map((campaignItem) => {
  const {
    campaign_id,
    dates,
    id,
    invoice_date,
    invoice_notes,
    invoice_reference,
    invoice_total,
    paid_by,
    price_override,
    title,
  } = campaignItem

  // @ts-expect-error TS2345
  const dateString = digObject(dates, 'created.date_time', '')

  // Dates and Timestamp for sort
  const parsedDate = DateTime.fromFormat(dateString, 'dd/MM/yyyy HH:mm')
  const sortTimeStamp = parsedDate.valueOf()
  const dueDateStringFormatted = parsedDate.toFormat('dd/MM/yyyy')

  return {
    actualTotal: invoice_total || 0,
    balanced: invoice_total === price_override,
    campaign_id,
    comments: invoice_notes,
    date: dueDateStringFormatted,
    editable: true,
    entityType: 'CampaignItem',
    expectedTotal: price_override,
    id,
    invoice_date,
    invoiceNumber: invoice_reference,
    invoice_notes,
    invoice_reference,
    invoice_total,
    itemCount: 1,
    itemType: 'campaign-item',
    orderNumber: `ITEM-${id}`,
    paid_by,
    provider: 'Custom',
    purchaseOrder: '',
    sort: sortTimeStamp,
    status: 'Approved',
    title,
  }
})

type MapOrders = {
  filteredOrders: OrderModel[],
  options: {
    campaigns: EntitiesState['campaigns'],
    providers: EntitiesState['providers'],
    statuses: EntitiesState['statuses'],
  },
}

const mapOrders = (params: MapOrders) => {
  const { filteredOrders, options } = params

  const { campaigns, providers, statuses } = options

  return filteredOrders?.map((order) => {
    const {
      campaign_id,
      dates,
      id,
      invoice_date,
      invoice_notes,
      invoice_reference,
      invoice_total,
      items,
      paid_by,
      provider_id,
      purchase_order,
      status_id,
      title,
      total,
    } = order

    const provider = providers[provider_id] || {}
    const status = statuses[status_id] || {}
    const campaign = campaigns[campaign_id] || {}

    // Dates and Timestamp for sort
    const approvalDateString = digObject(dates, 'approval.date_time', '')
    const formattedDate = approvalDateString ? formatDate(approvalDateString, 'dd/MM/yyyy HH:mm', 'dd/MM/yyyy') : 'N/A'
    const sort = approvalDateString ? formatDate(approvalDateString, 'dd/MM/yyyy HH:mm')
      : DateTime.now().plus({ days: 1 }).toFormat('yyyy-MM-dd')

    return {
      actualTotal: invoice_total || 0,
      balanced: invoice_total === total,
      campaign_id,
      campaignTitle: campaign.title || 'Unknown',
      comments: invoice_notes,
      date: formattedDate,
      editable: true,
      entityType: 'Order',
      expectedTotal: total,
      id,
      invoice_date,
      invoiceNumber: invoice_reference,
      invoice_notes,
      invoice_reference,
      invoice_total,
      itemCount: items ? items.length : 0,
      itemType: 'order',
      orderNumber: `ORDER-${id}`,
      paid_by,
      provider: provider.title || 'Unknown',
      purchaseOrder: purchase_order,
      sort,
      status: status.title || 'Approved',
      title,
    }
  })
}

type CalculateProcessingFees = {
  filteredOrders: OrderModel[],
  filteredOrderItems: OrderItemModel[],
}

const calculateProcessingFees = (params: CalculateProcessingFees) => {
  const { filteredOrders, filteredOrderItems } = params

  const fees = {
    count: 0,
    total: 0,
  }

  filteredOrders.forEach((order) => {
    const items = filteredOrderItems.filter(item => item.order_id === order.id)
    if (items.length){
      fees.count += items.length
      fees.total += items.reduce((acc, item) => acc + (item.processing_fee || 0), 0)
    }
  })

  return fees
}

type MapProcessingFees = {
  processingFees: {
    count: number,
    total: number,
  },
}

const mapProcessingFees = (params: MapProcessingFees) => {
  const { processingFees } = params
  const { count, total } = processingFees

  return {
    actualTotal: total,
    balanced: true,
    comments: '',
    date: 'N/A',
    editable: false,
    entityType: 'ProcessingFee',
    expectedTotal: total,
    id: 'N/A',
    invoiceNumber: '',
    itemCount: count,
    itemType: 'processing-fees',
    orderNumber: 'N/A',
    provider: 'Realhub',
    purchaseOrder: '',
    sort: 0,
    status: 'Approved',
    title: 'Processing Fees',
  }
}

type MapMergeItemsParams = {
  filteredCampaignItems: CampaignItemModel[],
  filteredOrders: OrderModel[],
  processingFees: {
    count: number,
    total: number,
  },
  providerId?: number,
  options: {
    campaigns: EntitiesState['campaigns'],
    providers: EntitiesState['providers'],
    statuses: EntitiesState['statuses'],
  },
}

const mapMergeItems = (params: MapMergeItemsParams) => {
  const {
    filteredCampaignItems, filteredOrders, options, processingFees, providerId,
  } = params

  let items
  if (providerId){
    items = mapOrders({ filteredOrders, options })
  } else {
    items = [...mapCampaignItems(filteredCampaignItems), ...mapOrders({ filteredOrders, options })]
  }
  const sortedItems = sortArrayBy(items, 'asc', 'sort')

  if (!providerId){
    // @ts-expect-error TS2345
    sortedItems.push(mapProcessingFees({ processingFees }))
  }

  return sortedItems
}

type UpdateReconciliationItemParams = {
  dispatch: AppDispatch,
  item: ReconciliationItemModel,
  itemParams: Partial<ReconciliationItemModel>,
  requestOptions: EntityOptions,
}

const updateItem = (params: UpdateReconciliationItemParams) => {
  const {
    dispatch, item, itemParams, requestOptions,
  } = params

  const { entityType } = item
  const updateFn = entityType === 'CampaignItem' ? updateCampaignItem : updateOrder

  // @ts-expect-error TS2345
  return dispatch(updateFn(itemParams, requestOptions))
}

type useOrderReconciliationParams = {
  agency_id?: number,
  approval_date_start?: string,
  approval_date_end?: string,
  campaign_id?: number,
  orderStatusGroupKey: 'agency_orders_not_pending' | 'complete' | 'pending',
  provider_id?: number,
}

const useOrderReconciliation = (params: useOrderReconciliationParams) => {
  const {
    agency_id: agencyId,
    approval_date_start: approvalDateStart,
    approval_date_end: approvalDateEnd,
    campaign_id: campaignId,
    orderStatusGroupKey,
    provider_id: providerId,
  } = params

  const campaignItemsPayload = useCampaignItems({
    filters: {
      campaign_id: campaignId,
    },
    performHttpRequests: !!campaignId,
  })

  const orderPayload = useOrders({
    filters: {
      approval_date_end: providerId ? approvalDateEnd : undefined,
      approval_date_start: providerId ? approvalDateStart : undefined,
      campaign_agency_id: providerId ? agencyId : undefined,
      campaign_id: campaignId,
      order_by: 'id desc',
      owner_type: 'Agency',
      provider_id: providerId,
      status_group_key: orderStatusGroupKey,
    },
    requestOptions: {
      include_order_campaign: !!providerId,
      // @ts-expect-error TS2561
      include_order_item_campaign_id: true,
      include_order_items: true,
      include_order_provider: true,
      include_order_publication: true,
      include_order_status: true,
    },
    performHttpRequests: !!campaignId || !!providerId,
  })

  const campaignContributions = useCampaignContributions({
    filters: { campaign_id: campaignId },
    performHttpRequests: !!campaignId,
  })

  const creditNotesPayload = useCreditNotes({ filters: { campaign_id: campaignId }, performHttpRequests: !!campaignId })

  const orderItemsPayload = useOrderItems({
    filters: { campaign_id: campaignId },
  })

  const { filteredCampaignItems, loading: loadingCampaignItems } = campaignItemsPayload
  const { filteredOrders, loading: loadingOrders } = orderPayload
  const { filteredCampaignContributions, loading: loadingCampaignContributions } = campaignContributions
  const { filteredCreditNotes, loading: loadingCreditNotes } = creditNotesPayload
  const { filteredOrderItems, loading: loadingOrderItems } = orderItemsPayload

  // Campaign Items
  const campaignItemTotal = filteredCampaignItems.reduce(
    (acc, item) => acc + ((item.invoice_total) || 0),
    0,
  )
  const expectedCampaignItemTotal = filteredCampaignItems.reduce((acc, item) => acc + (item.price_override || 0), 0)

  // Contribution Totals
  const contributionTotal = filteredCampaignContributions.reduce((acc, item) => acc + (item.amount || 0), 0)
  const contributionsReceived = filteredCampaignContributions.reduce(
    (acc, item) => (item.payment_received ? acc + (item.amount || 0) : acc),
    0,
  )

  // Orders
  const orderTotal = filteredOrders.reduce((acc, order) => acc + (order.invoice_total || 0), 0)
  const expectedOrderTotal = filteredOrders.reduce((acc, order) => acc + order.total, 0)

  // Credit Note Total
  const creditNoteTotal = filteredCreditNotes.reduce((acc, item) => acc + item.total, 0)

  // Processing Fees
  const processingFees = calculateProcessingFees({ filteredOrders, filteredOrderItems })
  const { total: processingFeeTotal } = processingFees

  // Totals
  const actualTotal = campaignItemTotal + orderTotal + processingFeeTotal
  const expectedTotal = expectedCampaignItemTotal + expectedOrderTotal + processingFeeTotal

  const difference = expectedTotal - actualTotal - creditNoteTotal
  const credits = contributionsReceived + creditNoteTotal
  const balance = credits - actualTotal
  const inCredit = balance >= 0

  const { campaigns, providers, statuses } = useSelector(reduxState => reduxState.entities)

  // group the orders and campaign items to a custom object
  const orderReconciliationItems = {
    items: mapMergeItems({
      filteredCampaignItems, filteredOrders, processingFees, providerId, options: { campaigns, providers, statuses },
    }),
    totals: {
      actualTotal,
      contributionTotal,
      contributionsReceived,
      creditNoteTotal,
      expectedTotal,
    },
  }
  const { items } = orderReconciliationItems

  const hasReconciliationItems = !!orderReconciliationItems

  const loadingOrderReconciliationItems = loadingCampaignItems
  || loadingOrders
  || loadingCampaignContributions
  || loadingCreditNotes
  || loadingOrderItems

  const { callbacks } = useContext(PageContext)
  const dispatch = useThunkDispatch()

  const {
    callbacks: {
      deselectAll, toggleId, selectAll, setSelectedIds,
    },
    hasSelectedEntities,
    selectedEntityIds,
  } = useSelectEntities()

  useEffect(() => {
    deselectAll()
  }, [approvalDateStart, approvalDateEnd, campaignId, providerId])

  return {
    balance,
    callbacks: {
      bulkReconcile: (reconcilePayload: {
        entityType: string,
        selectedEntityIds: number[],
      }) => launchModal({
        callbacks,
        modalKey: 'BulkReconcileModal',
        payload: { callbacks: { deselectAll }, ...reconcilePayload },
      }),
      deselectAll,
      editReconciliationItem: (item: (CampaignItemModel | OrderModel)) => launchModal({
        callbacks,
        modalKey: 'EditReconciliationItemModal',
        payload: { item },
      }),
      setSelectedIds,
      selectAllItems: (
        selectedEntityType: string,
      // @ts-expect-error TS2322
      ) => selectAllItems({ items, selectAll, selectedEntityType }),
      toggleId,
      updateItem: (
        item: ReconciliationItemModel,
        itemParams: Partial<ReconciliationItemModel>,
      // @ts-expect-error TS2345
      ) => updateItem({ dispatch, item, itemParams }),
    },
    credits,
    difference,
    hasReconciliationItems,
    hasSelectedEntities,
    inCredit,
    loadingOrderReconciliationItems,
    orderPayload,
    orderReconciliationItems,
    selectedEntityIds,
  }
}

export type OrderReconciliationPayload = ReturnType<typeof useOrderReconciliation>

export default useOrderReconciliation
